import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  Avatar,
  Typography,
  IconButton,
  TableSortLabel,
  TablePagination,
  Tooltip,
  CircularProgress,
  Button,
  Menu,
  MenuItem,
  ListItemIcon
} from '@mui/material';
import {
  OpenInNewIcon,
  RefreshIcon,
  MoreVertIcon,
  EditOutlinedIcon,
  SaveAltOutlinedIcon,
  EmailOutlinedIcon,
  DeleteOutlineIcon
} from 'icons/materialIcons';
import avatarPlaceholder from 'images/avatar-cropped.png';
import PropTypes from 'prop-types';
import UpgradeSnackBar from 'components/common/notification/UpgradeSnackBar';
import { getCardById } from 'apis/cardApi';
import { useAuth } from 'contexts/AuthContext';
import { deleteLead } from 'apis/leadCaptureApi';
import { useLeadCaptureContext } from 'contexts/LeadCaptureContext';
import { downloadVCard } from 'utils/vcfUtils';
import RemoveLeadModal from 'components/contacts/contactDetails/RemoveLeadModal';
import SendLeadEmailModal from 'components/contacts/contactDetails/SendLeadEmailModal';

import ContactEmailCell from 'components/contacts/contactMain/contactsTable/ContactEmailCell';
import LeadSourceChip from 'components/contacts/contactMain/contactsTable/LeadSourceChip';
import ExportModal from 'components/contacts/contactMain/contactsMenu/ExportModal';

const ContactsTable = ({
  contacts = [],
  onContactClick,
  isLoading = false,
  onRefresh,
  onSelectContacts,
  selectedContactIds = []
}) => {
  const { authState } = useAuth();
  const { deleteContact } = useLeadCaptureContext();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selected, setSelected] = useState(selectedContactIds || []);
  const [orderBy, setOrderBy] = useState('date');
  const [order, setOrder] = useState('desc');
  const [showUpgradeSnackbar, setShowUpgradeSnackbar] = useState(false);
  const [connectedCards, setConnectedCards] = useState({});
  const [exportMenuAnchorEl, setExportMenuAnchorEl] = useState(null);
  const [activeContact, setActiveContact] = useState(null);
  const [exportMenuOpen, setExportMenuOpen] = useState(false);

  // State for options menu and deletion
  const [optionsMenuAnchorEl, setOptionsMenuAnchorEl] = useState(null);
  const [activeContactForOptions, setActiveContactForOptions] = useState(null);
  const [hoveredRow, setHoveredRow] = useState(null);
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Email modal state
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);

  // Add loading state for card data
  const [isLoadingCardData, setIsLoadingCardData] = useState(false);

  // Check if contacts is a valid array
  const isValidContactsArray = Array.isArray(contacts) && contacts.length > 0;

  // Memoize contact card IDs to prevent unnecessary re-renders
  const contactCardIds = useMemo(() => {
    return authState?.user?.contactCardIds || [];
  }, [authState?.user?.contactCardIds]);

  // Memoize default card ID to prevent unnecessary re-renders
  const defaultCardId = useMemo(() => {
    if (!contactCardIds.length) return null;
    return contactCardIds.find(id => authState.cardState?.[id]?.is_default) || contactCardIds[0];
  }, [contactCardIds, authState.cardState]);

  // Sync selected state with parent component
  useEffect(() => {
    if (selectedContactIds && selectedContactIds.length > 0 && JSON.stringify(selectedContactIds) !== JSON.stringify(selected)) {
      setSelected(selectedContactIds);
    }
  }, [selectedContactIds]);

  // Notify parent component when selections change
  useEffect(() => {
    if (onSelectContacts) {
      onSelectContacts(selected);
    }
  }, [selected, onSelectContacts]);

  useEffect(() => {
    const fetchCardData = async () => {
      if (!isValidContactsArray || !authState.token) return;

      // Prevent duplicate fetches
      if (isLoadingCardData) return;

      // Find contacts that need card data
      const contactsNeedingCardData = contacts.filter(contact =>
        contact._id && !connectedCards[contact._id]
      );

      if (contactsNeedingCardData.length === 0) return;

      setIsLoadingCardData(true);

      // Start with existing card data to avoid losing previously fetched data
      const cardMap = { ...connectedCards };

      // Track unique card IDs to avoid duplicate API calls
      const uniqueCardIds = new Set();
      const cardDataCache = {};

      try {
        // First, collect all unique card IDs
        contactsNeedingCardData.forEach(contact => {
          const cardId = contact.cardId || defaultCardId;
          if (cardId) {
            uniqueCardIds.add(cardId);
          }
        });

        // Fetch each unique card only once
        for (const cardId of uniqueCardIds) {
          try {
            const response = await getCardById(cardId);
            if (response && response.success && response.card) {
              cardDataCache[cardId] = {
                name: response.card.contact_info?.fullName || 'Unnamed Card',
                avatar: response.card.profilePictureUrl || avatarPlaceholder,
                cardId: cardId
              };
            }
          } catch (error) {
          }
        }

        // Apply cached card data to contacts
        contactsNeedingCardData.forEach(contact => {
          const cardId = contact.cardId || defaultCardId;
          if (cardId && cardDataCache[cardId]) {
            cardMap[contact._id] = cardDataCache[cardId];
          }
        });

        // Only update state if we have new data
        if (Object.keys(cardMap).length > Object.keys(connectedCards).length) {
          setConnectedCards(cardMap);
        }
      } finally {
        setIsLoadingCardData(false);
      }
    };

    fetchCardData();
  }, [contacts, authState.token, defaultCardId, isValidContactsArray]);

  // Loading state
  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '300px' }}>
        <CircularProgress sx={{ mb: 2 }} />
        <Typography variant="body1" color="text.secondary">Loading contacts...</Typography>
      </Box>
    );
  }

  // Empty or invalid state
  if (!isValidContactsArray) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '300px', border: '1px dashed #ccc', borderRadius: '8px' }}>
        <Typography variant="h6">No contacts found</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Add your first contact by clicking the 'Add Leads' button
        </Typography>
        {onRefresh && (
          <Button
            startIcon={<RefreshIcon />}
            variant="outlined"
            onClick={onRefresh}
            sx={{ mt: 2 }}
          >
            Refresh
          </Button>
        )}
      </Box>
    );
  }

  const getInitials = (name) => {
    if (!name) return '';
    return name
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase();
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelected = contacts.map(n => n._id);
      setSelected(newSelected);
      return;
    }
    setSelected([]);
  };

  const handleClick = (event, contact) => {
    if (!contact || !contact._id) {
      return;
    }

    if (event.target.closest('.MuiCheckbox-root') || event.target.closest('[data-copy-button]')) {
      const selectedIndex = selected.indexOf(contact._id);
      let newSelected = [];

      if (selectedIndex === -1) {
        newSelected = newSelected.concat(selected, contact._id);
      } else if (selectedIndex === 0) {
        newSelected = newSelected.concat(selected.slice(1));
      } else if (selectedIndex === selected.length - 1) {
        newSelected = newSelected.concat(selected.slice(0, -1));
      } else if (selectedIndex > 0) {
        newSelected = newSelected.concat(
          selected.slice(0, selectedIndex),
          selected.slice(selectedIndex + 1),
        );
      }

      setSelected(newSelected);
    } else if (!event.target.closest('.MuiIconButton-root')) {
      onContactClick && onContactClick(contact);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleRequestSort = () => {
    const isAsc = orderBy === 'date' && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy('date');
  };

  const handleCopyEmail = async (e, email, id) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(email);
    } catch (err) {
    }
  };

  // Prepare contact data for vCard download
  const prepareContactData = (contact) => {
    return {
      contact_info: {
        fullName: contact.leadName,
        email: contact.leadEmail,
        phoneNumber: contact.leadPhoneNumber,
        company: contact.company || contact.leadCompany,
        jobTitle: contact.jobTitle || contact.leadJobTitle,
        location: contact.leadAddress
      },
      bio: contact.leadNotes,
      profilePictureUrl: contact.profilePictureUrl
    };
  };

  // Handle export button click to open menu
  const handleExportButtonClick = (event, contact) => {
    event.stopPropagation();
    setActiveContact(contact);
    setExportMenuAnchorEl(event.currentTarget);
    setExportMenuOpen(true);
  };

  // Handle export menu close
  const handleExportMenuClose = () => {
    setExportMenuAnchorEl(null);
    setExportMenuOpen(false);
    setActiveContact(null);
  };

  // Handle options button click to open menu
  const handleOptionsButtonClick = (event, contact) => {
    event.stopPropagation();
    setActiveContactForOptions(contact);
    setOptionsMenuAnchorEl(event.currentTarget);
  };

  // Handle options menu close
  const handleOptionsMenuClose = () => {
    setOptionsMenuAnchorEl(null);
  };

  // Handlers for options menu actions
  const handleEditConnection = () => {
    handleOptionsMenuClose();
    if (activeContactForOptions && onContactClick) {
      onContactClick(activeContactForOptions, true);
    }
  };

  const handleSaveAsContact = () => {
    handleOptionsMenuClose();
    if (activeContactForOptions) {
      try {
        const contactData = prepareContactData(activeContactForOptions);
        downloadVCard(contactData);
      } catch (error) {
      }
    }
  };

  const handleSendEmail = () => {
    handleOptionsMenuClose();
    if (activeContactForOptions) {
      setIsEmailModalOpen(true);
    }
  };

  const handleRemoveClick = () => {
    handleOptionsMenuClose();
    setShowRemoveModal(true);
  };

  const handleCancelRemove = () => {
    setShowRemoveModal(false);
  };

  const handleConfirmRemove = async () => {
    try {
      setIsDeleting(true);

      if (activeContactForOptions && activeContactForOptions._id) {
        await deleteLead(activeContactForOptions._id);

        deleteContact(activeContactForOptions._id);

        if (onRefresh) {
          onRefresh();
        }

        setShowRemoveModal(false);
      }
    } catch (error) {
      alert('Failed to delete lead. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  // Sort contacts by date
  const sortedContacts = [...contacts].sort((a, b) => {
    if (!a.createdAt || !b.createdAt) return 0;

    if (order === 'asc') {
      return new Date(a.createdAt) - new Date(b.createdAt);
    }
    return new Date(b.createdAt) - new Date(a.createdAt);
  });

  const emptyRows = page > 0
    ? Math.max(0, (1 + page) * rowsPerPage - contacts.length)
    : 0;

  // Row hover handlers
  const handleRowMouseEnter = (contactId) => {
    setHoveredRow(contactId);
  };

  const handleRowMouseLeave = () => {
    setHoveredRow(null);
  };

  return (
    <Box sx={{ width: '100%', overflow: 'auto' }}>
      <Paper
        sx={{
          width: '100%',
          mt: 2,
          borderRadius: '10px',
          overflow: 'hidden',
          minWidth: '1000px',
          backgroundColor: 'white',
          boxShadow: 'none'
        }}
      >
        <TableContainer>
          <Table aria-labelledby="tableTitle" sx={{ minWidth: 750 }}>
            <TableHead>
              <TableRow
                sx={{
                  backgroundColor: 'white',
                  borderBottom: '1px solid #f2f2f2',
                  '& th': {
                    borderBottom: '1px solid #f2f2f2',
                    backgroundColor: 'white',
                    color: '#828283',
                    fontWeight: 600
                  }
                }}
              >
                <TableCell padding="checkbox" sx={{ width: '140px' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Checkbox
                      color="primary"
                      indeterminate={selected.length > 0 && selected.length < contacts.length}
                      checked={contacts.length > 0 && selected.length === contacts.length}
                      onChange={handleSelectAllClick}
                      inputProps={{ 'aria-label': 'select all contacts' }}
                      sx={{ color: '#828282' }}
                    />
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#828283', userSelect: 'none', whiteSpace: 'nowrap' }}>
                      Select all
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#828283', minWidth: '200px' }}>Contact</TableCell>
                <TableCell align="center" sx={{ fontWeight: 600, color: '#828283', minWidth: '150px' }}>Connected with</TableCell>
                <TableCell align="center" sx={{ fontWeight: 600, color: '#828283', minWidth: '120px' }}>Type</TableCell>
                <TableCell align="center" sx={{ fontWeight: 600, color: '#828283', minWidth: '120px', whiteSpace: 'nowrap' }}>
                  <TableSortLabel
                    active={orderBy === 'date'}
                    direction={order}
                    onClick={handleRequestSort}
                    sx={{
                      color: '#828283 !important',
                      '&.Mui-active': { color: '#828283 !important' },
                      '& .MuiTableSortLabel-icon': { color: '#828283 !important' }
                    }}
                  >
                    Date
                  </TableSortLabel>
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: 600, color: '#828283', minWidth: '80px' }}>Export</TableCell>
                <TableCell sx={{ width: '40px', padding: 0 }}></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedContacts
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((contact) => {
                  if (!contact || !contact._id) {
                    return null;
                  }

                  const isItemSelected = selected.indexOf(contact._id) !== -1;
                  const isRowHovered = hoveredRow === contact._id;

                  return (
                    <TableRow
                      hover
                      onClick={(event) => handleClick(event, contact)}
                      role="checkbox"
                      aria-checked={isItemSelected}
                      tabIndex={-1}
                      key={contact._id}
                      selected={isItemSelected}
                      onMouseEnter={() => handleRowMouseEnter(contact._id)}
                      onMouseLeave={handleRowMouseLeave}
                      sx={{
                        '&:last-child td, &:last-child th': { border: 0 },
                        cursor: 'pointer',
                        backgroundColor: 'white',
                        borderBottom: '1px solid #f2f2f2',
                        '&:hover': {
                          backgroundColor: '#f5f5f5'
                        }
                      }}
                    >
                      <TableCell padding="checkbox">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Checkbox checked={isItemSelected} sx={{ color: '#828282' }} />
                          <Avatar
                            src={contact.profilePictureUrl || null}
                            sx={{
                              bgcolor: '#e0e0e0',
                              width: 40,
                              height: 40
                            }}
                          >
                            {!contact.profilePictureUrl && getInitials(contact.leadName)}
                          </Avatar>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                            {contact.leadName || contact.leadEmail || 'Unnamed Contact'}
                          </Typography>
                          <ContactEmailCell
                            email={contact.leadEmail || 'No Email'}
                            onCopyEmail={handleCopyEmail}
                            contactId={contact._id}
                          />
                        </Box>
                      </TableCell>

                      <TableCell align="center">
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          {isLoadingCardData ? (
                            <CircularProgress size={20} sx={{ color: '#828282' }} />
                          ) : (
                            <Tooltip
                              title={connectedCards[contact._id]?.name || `${authState?.user?.fullName || 'Your'} Card`}
                              placement="top"
                              arrow
                            >
                              <Avatar
                                src={connectedCards[contact._id]?.avatar || avatarPlaceholder}
                                sx={{
                                  width: 32,
                                  height: 32,
                                  transition: 'transform 0.2s ease-in-out',
                                  '&:hover': {
                                    transform: 'scale(1.1)',
                                    transformOrigin: 'center'
                                  }
                                }}
                              />
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <LeadSourceChip source={contact.leadSource || 'manually-added'} />
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2" color="text.secondary">
                          {contact.createdAt
                            ? new Date(contact.createdAt).toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric'
                            })
                            : 'N/A'
                          }
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <IconButton
                          size="small"
                          onClick={(e) => handleExportButtonClick(e, contact)}
                          sx={{
                            backgroundColor: '#f5f5f5',
                            borderRadius: '20px',
                            padding: '8px',
                            '&:hover': {
                              backgroundColor: '#e0e0e0'
                            }
                          }}
                        >
                          <OpenInNewIcon sx={{ fontSize: 18, color: '#828282' }} />
                        </IconButton>
                      </TableCell>
                      <TableCell sx={{ padding: 0, width: '40px' }}>
                        <IconButton
                          size="small"
                          onClick={(e) => handleOptionsButtonClick(e, contact)}
                          sx={{
                            backgroundColor: '#f5f5f5',
                            borderRadius: '20px',
                            padding: '8px',
                            opacity: isRowHovered ? 1 : 0,
                            transition: 'opacity 0.2s',
                            '&:hover': {
                              backgroundColor: '#e0e0e0'
                            }
                          }}
                        >
                          <MoreVertIcon sx={{ fontSize: 18, color: '#828282' }} />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
              {emptyRows > 0 && (
                <TableRow style={{ height: 53 * emptyRows }}>
                  <TableCell colSpan={7} />
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={contacts.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{
            borderTop: '1px solid #f2f2f2',
            backgroundColor: 'white'
          }}
        />
      </Paper>

      <ExportModal
        isOpen={exportMenuOpen}
        onClose={handleExportMenuClose}
        anchorEl={exportMenuAnchorEl}
        contacts={activeContact}
        isSingleContact={true}
        positionAdjustment={{ horizontal: '20px' }}
      />

      <Menu
        anchorEl={optionsMenuAnchorEl}
        open={Boolean(optionsMenuAnchorEl)}
        onClose={handleOptionsMenuClose}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            borderRadius: '20px',
            boxShadow: '0px 4px 16px rgba(0, 0, 0, 0.08)',
            width: '200px',
            position: 'absolute',
            left: 'auto',
            marginLeft: '-60px',
            marginTop: '0px',
          }
        }}
        MenuListProps={{
          sx: {
            padding: '8px',
          }
        }}
        container={() => document.querySelector('body')}
        keepMounted
      >
        <MenuItem
          onClick={handleEditConnection}
          sx={{
            py: 1.5,
            borderRadius: '12px',
            '&:hover': {
              backgroundColor: '#f5f5f5',
            },
          }}
        >
          <ListItemIcon sx={{ minWidth: 36 }}>
            <EditOutlinedIcon sx={{ fontSize: 20 }} />
          </ListItemIcon>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 600,
              flexGrow: 1,
              paddingRight: '10px',
            }}
          >
            Edit connection
          </Typography>
        </MenuItem>
        <MenuItem
          onClick={handleSaveAsContact}
          sx={{
            py: 1.5,
            borderRadius: '12px',
            '&:hover': {
              backgroundColor: '#f5f5f5',
            },
          }}
        >
          <ListItemIcon sx={{ minWidth: 36 }}>
            <SaveAltOutlinedIcon sx={{ fontSize: 20 }} />
          </ListItemIcon>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 600,
              flexGrow: 1,
              paddingRight: '10px',
            }}
          >
            Save as contact
          </Typography>
        </MenuItem>
        <MenuItem
          onClick={handleSendEmail}
          sx={{
            py: 1.5,
            borderRadius: '12px',
            '&:hover': {
              backgroundColor: '#f5f5f5',
            },
          }}
        >
          <ListItemIcon sx={{ minWidth: 36 }}>
            <EmailOutlinedIcon sx={{ fontSize: 20 }} />
          </ListItemIcon>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 600,
              flexGrow: 1,
              paddingRight: '10px',
            }}
          >
            Send email
          </Typography>
        </MenuItem>
        <MenuItem
          onClick={handleRemoveClick}
          sx={{
            py: 1.5,
            borderRadius: '12px',
            '&:hover': {
              backgroundColor: '#f5f5f5',
            },
            color: '#d32f2f',
          }}
        >
          <ListItemIcon sx={{ minWidth: 36 }}>
            <DeleteOutlineIcon sx={{ fontSize: 20, color: '#d32f2f' }} />
          </ListItemIcon>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 600,
              flexGrow: 1,
              paddingRight: '10px',
              color: '#d32f2f',
            }}
          >
            Remove
          </Typography>
        </MenuItem>
      </Menu>

      {showRemoveModal && (
        <RemoveLeadModal
          open={showRemoveModal}
          onClose={handleCancelRemove}
          onConfirm={handleConfirmRemove}
          isDeleting={isDeleting}
        />
      )}

      <SendLeadEmailModal
        open={isEmailModalOpen}
        onClose={() => setIsEmailModalOpen(false)}
        contact={activeContactForOptions}
      />

      <UpgradeSnackBar
        open={showUpgradeSnackbar}
        handleClose={() => setShowUpgradeSnackbar(false)}
        handleSubscribe={() => {
          setShowUpgradeSnackbar(false);
        }}
      />
    </Box>
  );
};

ContactsTable.propTypes = {
  contacts: PropTypes.arrayOf(
    PropTypes.shape({
      _id: PropTypes.string,
      leadName: PropTypes.string,
      leadEmail: PropTypes.string,
      tags: PropTypes.string,
      userId: PropTypes.string,
      cardId: PropTypes.string,
      leadSource: PropTypes.string,
      createdAt: PropTypes.string
    })
  ),
  onContactClick: PropTypes.func,
  isLoading: PropTypes.bool,
  onRefresh: PropTypes.func,
  onSelectContacts: PropTypes.func,
  selectedContactIds: PropTypes.array
};

export default ContactsTable;